import React, { useEffect } from 'react';
import { useApp } from '../../contexts/AppContext';

const NotificationContainer = () => {
  const { notifications, removeNotification } = useApp();

  if (notifications.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-20 right-4 z-50 space-y-2 max-w-md w-full pointer-events-none">
      {notifications.map((notification) => (
        <NotificationToast
          key={notification.id}
          notification={notification}
          onRemove={() => removeNotification(notification.id)}
        />
      ))}
    </div>
  );
};

const NotificationToast = ({ notification, onRemove }) => {
  useEffect(() => {
    // Auto-remove notifications after 5 seconds if they're not persistent
    if (!notification.persistent) {
      const timer = setTimeout(() => {
        onRemove();
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [notification.persistent, onRemove]);

  const getNotificationStyles = (type) => {
    switch (type) {
      case 'success':
        return {
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          iconColor: 'text-green-600',
          titleColor: 'text-green-900',
          messageColor: 'text-green-800',
          icon: (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          )
        };
      case 'error':
        return {
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          iconColor: 'text-red-600',
          titleColor: 'text-red-900',
          messageColor: 'text-red-800',
          icon: (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          )
        };
      case 'warning':
        return {
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          iconColor: 'text-yellow-600',
          titleColor: 'text-yellow-900',
          messageColor: 'text-yellow-800',
          icon: (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z"
            />
          )
        };
      case 'info':
      default:
        return {
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          iconColor: 'text-blue-600',
          titleColor: 'text-blue-900',
          messageColor: 'text-blue-800',
          icon: (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          )
        };
    }
  };

  const styles = getNotificationStyles(notification.type);

  return (
    <div
      className={`
        w-full ${styles.bgColor} border ${styles.borderColor} rounded-lg shadow-lg
        pointer-events-auto ring-1 ring-black ring-opacity-5 transform transition-all duration-300
        ease-in-out animate-slide-up
      `}
    >
      <div className="p-4">
        <div className="flex items-start">
          {/* Icon */}
          <div className="flex-shrink-0">
            <svg
              className={`h-5 w-5 ${styles.iconColor}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {styles.icon}
            </svg>
          </div>

          {/* Content */}
          <div className="ml-3 flex-1 min-w-0">
            {notification.title && (
              <p className={`text-sm font-medium ${styles.titleColor}`}>
                {notification.title}
              </p>
            )}
            <p
              className={`text-sm ${notification.title ? 'mt-1' : ''} ${styles.messageColor} break-words`}
            >
              {notification.message}
            </p>
          </div>

          {/* Close button */}
          <div className="ml-4 flex-shrink-0 flex">
            <button
              onClick={onRemove}
              className={`
                inline-flex ${styles.iconColor} hover:${styles.titleColor}
                focus:outline-none focus:${styles.titleColor} transition-colors duration-200
              `}
            >
              <span className="sr-only">Cerrar</span>
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Progress bar for auto-remove notifications */}
        {!notification.persistent && (
          <div className="mt-3">
            <div className="w-full bg-gray-200 rounded-full h-1">
              <div
                className="bg-gray-400 h-1 rounded-full animate-progress"
                style={{ animationDuration: '5s' }}
              ></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationContainer;